import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import apiClient from '../utils/api';
import { COUNTRY_CODES } from '../constants/phoneCountryCodes';
import { extractAxiosErrorMessage } from '../utils/errorUtils';
import SMSConsentDisclosure from './SMSConsentDisclosure';

interface SMSMFASetupProps {
  onComplete: () => void;
  onCancel: () => void;
}

const SMSMFASetup: React.FC<SMSMFASetupProps> = ({ onComplete, onCancel }) => {
  const [step, setStep] = useState<'phone' | 'verify'>('phone');
  const [countryCode, setCountryCode] = useState('+1');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [maskedPhone, setMaskedPhone] = useState<string>('');

  const formatPhoneNumber = (phone: string, country: string): string => {
    // Remove all non-digit characters
    const cleaned = phone.replace(/\D/g, '');
    // Strip any existing country code from the phone number
    // Just use digits, prepend the selected country code
    return `${country}${cleaned}`;
  };

  const validatePhoneNumber = (phone: string): boolean => {
    // Basic validation: should be at least 10 digits (most countries require 10+)
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length >= 10;
  };

  const handleSendCode = async () => {
    if (!validatePhoneNumber(phoneNumber)) {
      setError('Please enter a valid phone number');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const formattedPhone = formatPhoneNumber(phoneNumber, countryCode);
      const response = await apiClient.post('/api/sms-mfa/setup/start', {
        phoneNumber: formattedPhone,
      });

      if (response.data.success) {
        setMaskedPhone(response.data.phoneNumberMasked || formattedPhone);
        setStep('verify');
      } else {
        throw new Error(response.data.message || 'Failed to send verification code');
      }
    } catch (err) {
      setError(extractAxiosErrorMessage(err, 'Failed to send verification code'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit verification code');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // First verify the phone number
      const verifyResponse = await apiClient.post('/api/sms-mfa/setup/verify', {
        code: verificationCode,
      });

      if (!verifyResponse.data.success) {
        throw new Error(verifyResponse.data.message || 'Invalid verification code');
      }

      // Then enable SMS MFA
      const enableResponse = await apiClient.post('/api/sms-mfa/enable');

      if (enableResponse.data.success) {
        onComplete();
      } else {
        throw new Error(enableResponse.data.message || 'Failed to enable SMS MFA');
      }
    } catch (err) {
      setError(extractAxiosErrorMessage(err, 'Failed to verify code'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const formattedPhone = formatPhoneNumber(phoneNumber, countryCode);
      const response = await apiClient.post('/api/sms-mfa/setup/start', {
        phoneNumber: formattedPhone,
      });

      if (response.data.success) {
        setError(null);
        setSuccessMessage('Verification code resent successfully');
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error(response.data.message || 'Failed to resend code');
      }
    } catch (err) {
      setError(extractAxiosErrorMessage(err, 'Failed to resend verification code'));
    } finally {
      setIsLoading(false);
    }
  };

  if (step === 'phone') {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 3 }} icon={<WarningIcon />}>
          <Typography variant="body2">
            <strong>Security Notice:</strong> SMS authentication is vulnerable to SIM swapping
            attacks. We recommend using an authenticator app for stronger security. Only use SMS if
            other methods are not available.
          </Typography>
        </Alert>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter your phone number to receive verification codes via SMS.
        </Typography>

        <SMSConsentDisclosure />

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <FormControl fullWidth margin="normal">
          <InputLabel id="country-code-label">Country Code</InputLabel>
          <Select
            labelId="country-code-label"
            id="country-code-select"
            value={countryCode}
            label="Country Code"
            onChange={(e) => setCountryCode(e.target.value)}
            disabled={isLoading}
          >
            {COUNTRY_CODES.map((country) => (
              <MenuItem key={country.code} value={country.code}>
                {country.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Phone Number"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
          margin="normal"
          placeholder="5551234567"
          helperText={
            phoneNumber.replace(/\D/g, '').length >= 10
              ? `Will send to: ${countryCode} (${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`
              : 'Enter 10-digit phone number (digits only)'
          }
          disabled={isLoading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <span data-testid="phone-country-code-prefix">{countryCode}</span>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendCode}
            disabled={isLoading || !validatePhoneNumber(phoneNumber)}
          >
            {isLoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Sending...
              </>
            ) : (
              'Send Verification Code'
            )}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Verify Your Phone Number
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        We sent a 6-digit verification code to <strong>{maskedPhone}</strong>
      </Typography>

      {error && (
        <Alert
          severity={error.includes('success') ? 'success' : 'error'}
          sx={{ mb: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        label="Verification Code"
        value={verificationCode}
        onChange={(e) => {
          const value = e.target.value.replace(/\D/g, '').slice(0, 6);
          setVerificationCode(value);
        }}
        margin="normal"
        inputMode="numeric"
        placeholder="000000"
        inputProps={{ maxLength: 6 }}
        disabled={isLoading}
        helperText="Enter the 6-digit code from your SMS"
      />

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Didn&apos;t receive the code?{' '}
          <Button
            variant="text"
            size="small"
            onClick={handleResendCode}
            disabled={isLoading}
            sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
          >
            Resend Code
          </Button>
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button
          variant="outlined"
          onClick={() => setStep('phone')}
          disabled={isLoading}
        >
          Change Number
        </Button>
        <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleVerifyCode}
          disabled={isLoading || verificationCode.length !== 6}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Verifying...
            </>
          ) : (
            'Verify & Enable SMS MFA'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default SMSMFASetup;
